import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { KqlQueryService, KqlQueryExample } from "./shared/kql-query.service";

@Component({
  selector: "app-kql-queries",
  templateUrl: "./kql-queries.component.html",
  styleUrls: ["./kql-queries.component.css"],
})
export class KqlQueriesComponent implements OnInit {
  queries: KqlQueryExample[] = [];
  isLoading = false;
  showEditModal = false;
  editForm: FormGroup;
  editingQuery: KqlQueryExample | null = null;

  constructor(
    private kqlQueryService: KqlQueryService,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {
    this.editForm = this.fb.group({
      description: ["", [Validators.required, Validators.maxLength(500)]],
      query: ["", [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.loadQueries();
  }

  loadQueries(): void {
    this.isLoading = true;
    this.kqlQueryService.list().subscribe({
      next: (queries) => {
        this.queries = queries;
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error("Failed to load queries.", "Error");
        this.isLoading = false;
      },
    });
  }

  openAddModal(): void {
    this.editingQuery = null;
    this.editForm.reset();
    this.showEditModal = true;
  }

  openEditModal(query: KqlQueryExample): void {
    this.editingQuery = query;
    this.editForm.patchValue({
      description: query.description,
      query: query.query,
    });
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingQuery = null;
    this.editForm.reset();
  }

  saveQuery(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const formValue = this.editForm.value;

    if (this.editingQuery) {
      this.kqlQueryService
        .update({
          id: this.editingQuery.id,
          description: formValue.description,
          query: formValue.query,
        })
        .subscribe({
          next: () => {
            this.toastr.success("Query updated successfully.", "Success");
            this.closeEditModal();
            this.loadQueries();
          },
          error: (error) => {
            this.toastr.error("Failed to update query.", "Error");
          },
        });
    } else {
      this.kqlQueryService
        .create({
          description: formValue.description,
          query: formValue.query,
        })
        .subscribe({
          next: () => {
            this.toastr.success("Query created successfully.", "Success");
            this.closeEditModal();
            this.loadQueries();
          },
          error: (error) => {
            this.toastr.error("Failed to create query.", "Error");
          },
        });
    }
  }

  deleteQuery(query: KqlQueryExample): void {
    if (!confirm(`Are you sure you want to delete "${query.description}"?`)) {
      return;
    }

    this.kqlQueryService.delete(query.id).subscribe({
      next: () => {
        this.toastr.success("Query deleted successfully.", "Success");
        this.loadQueries();
      },
      error: (error) => {
        this.toastr.error("Failed to delete query.", "Error");
      },
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  copyQueryToClipboard(query: string): void {
    navigator.clipboard.writeText(query).then(
      () => {
        this.toastr.success("Query copied to clipboard.", "Success");
      },
      () => {
        this.toastr.error("Failed to copy query.", "Error");
      }
    );
  }
}

